/**
 * Single source of truth for "how do we display a client's name in the UI?"
 *
 * Background: profiles.display_name is sometimes empty or null for clients
 * who completed onboarding before display_name became required, or who
 * signed up via flows that didn't write to profiles. Each component used
 * to do its own fallback chain and they drifted — AssignDialog only fell
 * back to "Sin nombre" while useCoachClients fell back to email first.
 * Marco saw "Sin nombre" everywhere in AssignDialog while the same
 * clients showed up correctly in his client list.
 *
 * Use this helper anywhere you need to render a client's name.
 */

export interface ProfileLike {
  display_name?: string | null;
}

export interface OnboardingLike {
  full_name?: string | null;
  email?: string | null;
}

const FALLBACK = 'Sin nombre';

/**
 * Returns the best available display name for a client by checking, in order:
 *   1. profiles.display_name
 *   2. client_onboarding_profiles.full_name
 *   3. client_onboarding_profiles.email
 *   4. "Sin nombre" (last resort)
 *
 * Each source is trimmed and treated as missing if it ends up empty.
 */
export function getClientDisplayName(
  profile?: ProfileLike | null,
  onboarding?: OnboardingLike | null
): string {
  const candidates = [
    profile?.display_name,
    onboarding?.full_name,
    onboarding?.email,
  ];
  for (const c of candidates) {
    const trimmed = c?.trim();
    if (trimmed) return trimmed;
  }
  return FALLBACK;
}
